import React, { useState, useEffect, useRef } from 'react';
import { OpsTask, OpsCategory, CRMUser, RecurFrequency } from '../../types';
import StatusPill from '../pm/StatusPill';
import PriorityBadge from '../pm/PriorityBadge';
import { Plus, Check, RefreshCw, X, Flag, ArrowLeft } from 'lucide-react';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredShortLabel,
  isActionRequiredExternal,
} from '../../utils/actionRequiredDisplay';
import UserAvatar from '../ui/UserAvatar';
import { defaultOpsStartForDue, stripOpsDay } from '../../utils/opsTaskSchedule';
import { filterOpsTaskEmployeeTeam } from '../../utils/userRoles';
import './OpsDetailPanel.css';
import './OpsCommentsPopout.css';

const CATEGORY_OPTIONS: { value: OpsCategory; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'maintenance', label: 'Maint.' },
  { value: 'ordering', label: 'Order' },
  { value: 'requests', label: 'Req.' },
  { value: 'other', label: 'Other' },
];

const RECUR_OPTIONS: { value: RecurFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: '2 wks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Qtr' },
];

export interface OpsTaskPopoutDetailProps {
  task: OpsTask;
  teamMembers: CRMUser[];
  onUpdate: (id: string, updates: Partial<OpsTask>) => void;
}

/** Compact, fixed-height task meta strip for the ops popout (no scroll). */
const OpsTaskPopoutDetail: React.FC<OpsTaskPopoutDetailProps> = ({ task, teamMembers, onUpdate }) => {
  const [description, setDescription] = useState(task.description || '');
  const [editingDesc, setEditingDesc] = useState(false);
  const [showAssignMenu, setShowAssignMenu] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const [actionMenuStep, setActionMenuStep] = useState<'list' | 'external'>('list');
  const [externalDraft, setExternalDraft] = useState('');

  const actionRef = useRef<HTMLDivElement>(null);
  const assignRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setDescription(task.description || '');
    setEditingDesc(false);
  }, [task]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (actionRef.current && !actionRef.current.contains(t)) setShowActionMenu(false);
      if (assignRef.current && !assignRef.current.contains(t)) setShowAssignMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!showActionMenu) {
      setActionMenuStep('list');
      setExternalDraft('');
    }
  }, [showActionMenu]);

  const toInputDate = (d?: Date) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const fromInputDate = (s: string): Date | undefined => {
    if (!s) return undefined;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const handleDescSave = () => {
    if (description !== (task.description || '')) onUpdate(task.id!, { description });
    setEditingDesc(false);
  };

  const toggleAssignee = (uid: string) => {
    const current = task.assignedTo || [];
    const updated = current.includes(uid) ? current.filter(id => id !== uid) : [...current, uid];
    onUpdate(task.id!, { assignedTo: updated });
  };

  const toggleRecurring = () => {
    if (task.recurring) {
      onUpdate(task.id!, { recurring: null } as unknown as Partial<OpsTask>);
    } else {
      onUpdate(task.id!, { recurring: { frequency: 'weekly' as RecurFrequency } });
    }
  };

  const activeTeam = filterOpsTaskEmployeeTeam(teamMembers);

  const anyMenuOpen = showAssignMenu || showActionMenu || statusMenuOpen || priorityMenuOpen;

  return (
    <div className={`ops-popout-task-detail${anyMenuOpen ? ' ops-popout-task-detail--menus-open' : ''}`}>
      <div className="ops-popout-desc-row">
        {editingDesc ? (
          <input
            className="ops-popout-desc-input"
            value={description}
            onChange={e => setDescription(e.target.value)}
            onBlur={handleDescSave}
            onKeyDown={e => { if (e.key === 'Enter') handleDescSave(); }}
            placeholder="Add description…"
            autoFocus
          />
        ) : (
          <button
            type="button"
            className={`ops-popout-desc${description ? '' : ' ops-popout-desc--empty'}`}
            onClick={() => setEditingDesc(true)}
          >
            {description || 'Add description…'}
          </button>
        )}
      </div>

      <div className="ops-popout-meta-row">
        <div className={`ops-popout-meta-host${statusMenuOpen ? ' pm-dropdown-host--open' : ''}`}>
          <StatusPill status={task.status} onChange={s => onUpdate(task.id!, { status: s })} onOpenChange={setStatusMenuOpen} />
        </div>
        <div className={`ops-popout-meta-host${priorityMenuOpen ? ' pm-dropdown-host--open' : ''}`}>
          <PriorityBadge priority={task.priority} onChange={p => onUpdate(task.id!, { priority: p })} onOpenChange={setPriorityMenuOpen} />
        </div>

        <select
          className="ops-popout-meta-select"
          value={task.category}
          onChange={e => onUpdate(task.id!, { category: e.target.value as OpsCategory })}
          aria-label="Category"
        >
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <input
          type="date"
          className="ops-popout-date ops-popout-date--start"
          value={toInputDate(task.startDate)}
          onChange={e => {
            const start = fromInputDate(e.target.value);
            if (!start) {
              onUpdate(task.id!, { startDate: undefined });
              return;
            }
            const updates: Partial<OpsTask> = { startDate: start };
            if (task.dueDate && stripOpsDay(start).getTime() > stripOpsDay(task.dueDate).getTime()) {
              updates.dueDate = start;
            }
            onUpdate(task.id!, updates);
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          aria-label="Start date"
          title="Start date"
        />
        <input
          type="date"
          className="ops-popout-date"
          value={toInputDate(task.dueDate)}
          onChange={e => {
            const due = fromInputDate(e.target.value);
            if (!due) {
              onUpdate(task.id!, { dueDate: undefined });
              return;
            }
            const updates: Partial<OpsTask> = { dueDate: due };
            if (!task.startDate) updates.startDate = defaultOpsStartForDue(due);
            else {
              const start = new Date(task.startDate);
              if (stripOpsDay(start).getTime() > stripOpsDay(due).getTime()) updates.startDate = due;
            }
            onUpdate(task.id!, updates);
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          aria-label="Due date"
          title="Due date (deadline)"
        />

        <div className={`ops-popout-assignees${showAssignMenu ? ' pm-dropdown-host--open' : ''}`} ref={assignRef}>
          {(task.assignedTo || []).slice(0, 3).map(uid => {
            const u = teamMembers.find(m => m.uid === uid);
            if (!u) return null;
            return (
              <button
                key={uid}
                type="button"
                className="ops-popout-avatar-btn"
                title={`${u.displayName} — click to remove`}
                onClick={() => toggleAssignee(uid)}
              >
                <UserAvatar className="ops-popout-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} />
              </button>
            );
          })}
          {(task.assignedTo?.length || 0) > 3 && (
            <span className="ops-popout-avatar-more">+{(task.assignedTo?.length || 0) - 3}</span>
          )}
          <button
            type="button"
            className="ops-popout-icon-btn"
            onClick={() => setShowAssignMenu(v => !v)}
            title="Assign"
            aria-label="Assign"
          >
            <Plus size={12} />
          </button>
          {showAssignMenu && (
            <div className="ops-detail-assign-menu ops-popout-flyout">
              {activeTeam.map(u => (
                <button
                  key={u.uid}
                  type="button"
                  className="ops-detail-assign-option"
                  onClick={() => { toggleAssignee(u.uid); }}
                >
                  <UserAvatar className="ops-detail-assignee-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} />
                  {u.displayName}
                  {(task.assignedTo || []).includes(u.uid) && <Check size={12} className="ops-detail-assign-check" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={`ops-popout-action${showActionMenu ? ' pm-dropdown-host--open' : ''}`} ref={actionRef}>
          {task.actionRequired ? (
            <button
              type="button"
              className={`ops-popout-action-chip${isActionRequiredExternal(task.actionRequired) ? ' ops-popout-action-chip--external' : ''}`}
              onClick={() => setShowActionMenu(v => !v)}
              title={getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
            >
              <Flag size={10} />
              <span>{getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}</span>
              <span
                className="ops-popout-action-clear"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdate(task.id!, { actionRequired: undefined, actionRequiredNote: undefined } as Partial<OpsTask>);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onUpdate(task.id!, { actionRequired: undefined, actionRequiredNote: undefined } as Partial<OpsTask>);
                  }
                }}
                aria-label="Clear action required"
              >
                <X size={10} />
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="ops-popout-icon-btn"
              onClick={() => setShowActionMenu(v => !v)}
              title="Set action required"
              aria-label="Set action required"
            >
              <Flag size={12} />
            </button>
          )}
          {showActionMenu && (
            <div className="ops-detail-assign-menu ops-popout-flyout">
              {actionMenuStep === 'external' ? (
                <div className="pm-action-external-panel" onClick={e => e.stopPropagation()}>
                  <button
                    type="button"
                    className="ops-detail-assign-option pm-action-external-back"
                    onClick={() => { setActionMenuStep('list'); setExternalDraft(''); }}
                  >
                    <ArrowLeft size={12} /> Back
                  </button>
                  <label className="pm-action-external-label">Who we&apos;re waiting on</label>
                  <input
                    className="pm-action-external-input"
                    value={externalDraft}
                    onChange={e => setExternalDraft(e.target.value)}
                    placeholder="e.g. county inspector"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && externalDraft.trim()) {
                        onUpdate(task.id!, {
                          actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                          actionRequiredNote: externalDraft.trim(),
                        });
                        setShowActionMenu(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="pm-action-external-save"
                    disabled={!externalDraft.trim()}
                    onClick={() => {
                      if (!externalDraft.trim()) return;
                      onUpdate(task.id!, {
                        actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                        actionRequiredNote: externalDraft.trim(),
                      });
                      setShowActionMenu(false);
                    }}
                  >
                    Save
                  </button>
                </div>
              ) : (
                <>
                  {activeTeam.map(u => (
                    <button
                      key={u.uid}
                      type="button"
                      className="ops-detail-assign-option"
                      onClick={() => { onUpdate(task.id!, { actionRequired: u.uid, actionRequiredNote: undefined }); setShowActionMenu(false); }}
                    >
                      <UserAvatar className="ops-detail-assignee-avatar" name={u.displayName} photoURL={u.photoURL} userId={u.uid} />
                      {u.displayName}
                      {task.actionRequired === u.uid && <Check size={12} className="ops-detail-assign-check" />}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="ops-detail-assign-option ops-detail-assign-option--external"
                    onClick={() => {
                      setActionMenuStep('external');
                      setExternalDraft(
                        task.actionRequired && isActionRequiredExternal(task.actionRequired)
                          ? (task.actionRequiredNote || '').trim()
                          : '',
                      );
                    }}
                  >
                    <Flag size={10} style={{ color: '#047857' }} />
                    External…
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`ops-popout-icon-btn${task.recurring ? ' ops-popout-icon-btn--active' : ''}`}
          onClick={toggleRecurring}
          title={task.recurring ? 'Recurring — click to turn off' : 'Make recurring'}
          aria-label="Recurring"
        >
          <RefreshCw size={12} />
        </button>
        {task.recurring && (
          <select
            className="ops-popout-meta-select ops-popout-recur-freq"
            value={task.recurring.frequency}
            onChange={e => onUpdate(task.id!, { recurring: { ...task.recurring, frequency: e.target.value as RecurFrequency } })}
            aria-label="Recurring frequency"
          >
            {RECUR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}

      </div>
    </div>
  );
};

export default OpsTaskPopoutDetail;
